import type { DueTone, FollowUp, FollowUpDisplay, FollowUpParsePreview, FollowUpPriority, FollowUpTab } from './types';
import { formatCalendarDate, formatMediumDate, formatShortDay, parseCalendarDate, startOfDay } from './dateUtils';

export function getFollowUpPriority(tone: DueTone): FollowUpPriority {
  if (tone === 'overdue') return 'critical';
  if (tone === 'today') return 'high';
  if (tone === 'done') return 'done';
  return 'normal';
}

export const PRIORITY_ROW_CLASS: Record<FollowUpPriority, string> = {
  critical: 'border-s-[3px] border-s-red-500 bg-red-50/50 hover:border-red-300',
  high: 'border-s-[3px] border-s-orange-500 bg-orange-50/50 hover:border-orange-300',
  normal: 'border-border bg-white hover:border-slate-300',
  done: 'border-border/60 bg-slate-50/80 opacity-75',
};

export const PRIORITY_CHIP_CLASS: Record<FollowUpPriority, string> = {
  critical: 'bg-red-50 text-red-700 border-red-200',
  high: 'bg-orange-50 text-orange-700 border-orange-200',
  normal: 'bg-slate-50 text-slate-600 border-slate-200',
  done: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export const PRIORITY_LABEL_KEY: Record<FollowUpPriority, string | null> = {
  critical: 'todayPanel.priorityOverdue',
  high: 'todayPanel.priorityToday',
  normal: null,
  done: null,
};

export function getWaitingOnLabel(item: FollowUp): string | undefined {
  if (item.waitingOnUser) {
    return `${item.waitingOnUser.firstName} ${item.waitingOnUser.lastName}`.trim();
  }
  return item.waitingOnName?.trim() || undefined;
}

export function getDueMeta(item: FollowUp, now = new Date()): { label: string; tone: DueTone } {
  if (item.status === 'DONE') {
    return { label: 'Done', tone: 'done' };
  }

  const today = startOfDay(now);

  if (!item.dueDate) {
    return { label: 'Today', tone: 'today' };
  }

  const due = parseCalendarDate(item.dueDate);
  const diffDays = Math.round((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return { label: `Overdue · ${formatShortDay(due)}`, tone: 'overdue' };
  }
  if (diffDays === 0) {
    return { label: 'Today', tone: 'today' };
  }
  if (diffDays === 1) {
    return { label: 'Tomorrow', tone: 'upcoming' };
  }
  if (diffDays <= 14) {
    return { label: formatShortDay(due), tone: 'upcoming' };
  }
  return { label: formatMediumDate(due), tone: 'upcoming' };
}

export function toFollowUpDisplay(item: FollowUp, now = new Date()): FollowUpDisplay {
  const { label, tone } = getDueMeta(item, now);
  return {
    ...item,
    dueLabel: label,
    dueTone: tone,
    priority: getFollowUpPriority(tone),
    waitingOn: getWaitingOnLabel(item),
  };
}

export function filterFollowUpsByTab(items: FollowUp[], tab: FollowUpTab, now = new Date()): FollowUpDisplay[] {
  const today = startOfDay(now);

  return items
    .map((item) => toFollowUpDisplay(item, now))
    .filter((item) => {
      if (tab === 'done') return item.status === 'DONE';
      if (item.status === 'DONE') return false;

      if (!item.dueDate) return tab === 'today';

      const due = parseCalendarDate(item.dueDate);
      if (tab === 'today') return due.getTime() <= today.getTime();
      return due.getTime() > today.getTime();
    })
    .sort((a, b) => {
      const rank = (p: FollowUpPriority) => ({ critical: 0, high: 1, normal: 2, done: 3 }[p]);
      const diff = rank(a.priority) - rank(b.priority);
      if (diff !== 0) return diff;
      return (a.dueDate || '').localeCompare(b.dueDate || '');
    });
}

export function getTodayFollowUps(items: FollowUp[], now = new Date()): FollowUpDisplay[] {
  return filterFollowUpsByTab(items, 'today', now);
}

export function countTodayScope(items: FollowUp[], now = new Date()): number {
  return getTodayFollowUps(items, now).length;
}

export function countOverdueScope(items: FollowUp[], now = new Date()): number {
  const today = startOfDay(now);
  return items.filter((item) => {
    if (item.status === 'DONE' || !item.dueDate) return false;
    return parseCalendarDate(item.dueDate).getTime() < today.getTime();
  }).length;
}

export function countUpcomingScope(items: FollowUp[], now = new Date()): number {
  return filterFollowUpsByTab(items, 'upcoming', now).length;
}

/** Subtitle under task title — e.g. "2 days overdue · Raj" or "Today". */
export function getFollowUpMetaLine(item: FollowUpDisplay, now = new Date()): string {
  const waitingSuffix = item.waitingOn ? ` · ${item.waitingOn}` : '';

  if (item.status === 'DONE' || item.dueTone === 'done') {
    return item.dueLabel;
  }

  if (item.dueTone === 'overdue' && item.dueDate) {
    const today = startOfDay(now);
    const due = parseCalendarDate(item.dueDate);
    const diffDays = Math.abs(Math.round((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
    const dayLabel = diffDays === 1 ? '1 day overdue' : `${diffDays} days overdue`;
    return `${dayLabel}${waitingSuffix}`;
  }

  if (item.dueTone === 'today') {
    return `Today${waitingSuffix}`;
  }

  return `${item.dueLabel}${waitingSuffix}`;
}

export function previewDueLabel(dueDate: string | null, now = new Date()): string {
  if (!dueDate) return 'Today';
  const fake: FollowUp = {
    id: 'preview',
    title: '',
    dueDate,
    status: 'ACTIVE',
    createdAt: now.toISOString(),
    updatedAt: now.toISOString(),
  };
  return getDueMeta(fake, now).label;
}

export function previewPriority(dueDate: string | null, now = new Date()): FollowUpPriority {
  if (!dueDate) return 'high';
  const fake: FollowUp = {
    id: 'preview',
    title: '',
    dueDate,
    status: 'ACTIVE',
    createdAt: now.toISOString(),
    updatedAt: now.toISOString(),
  };
  return getFollowUpPriority(getDueMeta(fake, now).tone);
}

export function parsePreviewToDisplay(preview: FollowUpParsePreview, now = new Date()) {
  const priority = previewPriority(preview.dueDate, now);
  return {
    title: preview.title,
    dueLabel: previewDueLabel(preview.dueDate, now),
    waitingOn: preview.waitingOnName,
    priority,
  };
}

export function tabForDueDate(dueDate: string | null, now = new Date()): FollowUpTab {
  if (!dueDate) return 'today';
  const today = startOfDay(now);
  const due = parseCalendarDate(dueDate);
  return due.getTime() > today.getTime() ? 'upcoming' : 'today';
}

export function todayCalendarKey(now = new Date()): string {
  return formatCalendarDate(now);
}
